<template>
  <v-card flat style="max-width: 100%">
    <!-- Average payment delay resume -->
    <v-alert
      :value="1"
      type="info"
      :color="options.colors[averagePaymentDelay > 0 ? 0 : 1]"
      style="width: 100%"
    >
      <v-label dark
        >Average payment delay:
        {{
          averagePaymentDelay > 1
            ? averagePaymentDelay + " days after due time"
            : Math.abs(averagePaymentDelay) + " days before due time"
        }}</v-label
      >
      <br />
      <strong>{{
        averagePaymentDelay > 1
          ? "This customer usually pays the bills after due time."
          : "This customer usually pays the bills before due time."
      }}</strong>
    </v-alert>

    <!-- Detailed Payment stats panel -->
    <v-expansion-panel class="mt-4">
      <v-expansion-panel-content>
        <template v-slot:header>
          <div>
            <v-icon small>mdi-finance</v-icon>
            <span class="font-weight-bold pl-2 pr-2"
              >Detailed Payment Stats</span
            >
            <v-label>- {{ selectedYear }}</v-label>
          </div>
        </template>
        <v-card>
          <v-window v-model="payementStatsWindowIndex">
            <v-window-item>
              <v-card flat class="px-4 pb-4 pt-0">
                <v-subheader>{{ payementStatsWindowTitles[0] }}</v-subheader>

                <v-data-table :headers="headers" :items="detailedData">
                  <template slot="items" slot-scope="props">
                    <td>{{ props.item.invoice_name }}</td>
                    <td>{{ props.item.last_transaction_name }}</td>
                    <td>{{ timeToDate(props.item.invoice_date) }}</td>
                    <td>
                      {{ timeToDate(props.item.expected_payment_date) }}
                    </td>
                    <td>
                      {{ timeToDate(props.item.last_payment_date) }}
                      <v-chip
                        style="position: relative; top: -3px"
                        dark
                        :color="options.colors[props.item.overdue ? 0 : 1]"
                      >
                        <span style="position: relative; top: -1px">{{
                          props.item.overdue ? "overdue" : "on time"
                        }}</span>
                      </v-chip>
                    </td>
                    <td>
                      <v-label>{{
                        props.item.payment_delay > 1
                          ? props.item.payment_delay + " days after due time"
                          : Math.abs(props.item.payment_delay) +
                            " days before due time"
                      }}</v-label>
                    </td>
                  </template>

                  <template v-slot:footer>
                    <td></td>
                    <td></td>
                    <td></td>
                    <td></td>
                    <td class="text-xs-right">Average:</td>

                    <td class="text-xs-left">
                      <strong>{{
                        averagePaymentDelay > 1
                          ? averagePaymentDelay + " days after due time"
                          : Math.abs(averagePaymentDelay) +
                            " days before due time"
                      }}</strong>
                    </td>
                  </template>
                </v-data-table>
              </v-card>
            </v-window-item>

            <v-window-item>
              <v-card flat class="px-4 pb-4 pt-0">
                <v-subheader>{{ payementStatsWindowTitles[1] }}</v-subheader>

                <slot></slot>
              </v-card>
            </v-window-item>

            <v-window-item>
              <v-card flat class="px-4 pb-4 pt-0">
                <v-subheader>{{ payementStatsWindowTitles[2] }}</v-subheader>

                <v-layout row wrap align-center v-if="!loading">
                  <v-flex xs12>
                    <apexchart
                      type="rangeBar"
                      width="100%"
                      :options="options"
                      :series="series"
                      :height="
                        Math.min(
                          120 +
                            22 *
                              Math.max(
                                series[0].data.length,
                                series[1].data.length
                              ),
                          420
                        )
                      "
                    />
                  </v-flex>
                </v-layout>

                <div class="pa-4 text-xs-center" v-if="loading">
                  <looping-rhombuses-spinner
                    style="margin: auto"
                    class="text-xs-center"
                    :animation-duration="1000"
                    :size="74"
                    color="#1976d2"
                  />
                  <h3 class="title font-weight-light my-2">Loading Chart...</h3>
                  <span class="caption grey--text">Please wait</span>
                </div>
              </v-card>
            </v-window-item>
          </v-window>

          <v-divider></v-divider>
          <v-card-actions>
            <v-layout row wrap justify-center align-center>
              <v-flex xs4>
                <v-btn
                  depressed
                  small
                  color="primary"
                  dark
                  @click="payementStatsWindowIndexPrevious()"
                >
                  <v-icon>mdi-chevron-left</v-icon>
                  <span>
                    {{
                      payementStatsWindowTitles[
                        payementStatsWindowIndex == 0
                          ? 2
                          : payementStatsWindowIndex - 1
                      ]
                    }}</span
                  >
                </v-btn>
              </v-flex>
              <v-flex xs4>
                <v-item-group
                  v-model="payementStatsWindowIndex"
                  class="text-xs-center"
                  mandatory
                >
                  <v-item v-for="n in 3" :key="`btn-${n}`">
                    <v-btn
                      color="primary"
                      slot-scope="{ active, toggle }"
                      :input-value="active"
                      icon
                      small
                      @click="toggle"
                    >
                      <v-icon
                        small
                        :color="
                          n - 1 == payementStatsWindowIndex
                            ? 'white'
                            : 'primary'
                        "
                        >mdi-record</v-icon
                      >
                    </v-btn>
                  </v-item>
                </v-item-group>
              </v-flex>
              <v-flex xs4 text-xs-right>
                <v-btn
                  small
                  depressed
                  color="primary"
                  dark
                  @click="payementStatsWindowIndexNext()"
                >
                  {{
                    payementStatsWindowTitles[
                      payementStatsWindowIndex == 2
                        ? 0
                        : payementStatsWindowIndex + 1
                    ]
                  }}
                  <v-icon>mdi-chevron-right</v-icon>
                </v-btn>
              </v-flex>
            </v-layout>
          </v-card-actions>
        </v-card>
      </v-expansion-panel-content>
    </v-expansion-panel>

    <!-- <json-viewer :value="detailedData"></json-viewer> -->
    <!-- <json-viewer :value="expeditionsIds"></json-viewer> -->
  </v-card>
</template>

<script>
import apexchart from "vue-apexcharts";
import { mapGetters } from "vuex";
import date from "../../../mixins/date";

import JsonViewer from "vue-json-viewer";
import { LoopingRhombusesSpinner } from "epic-spinners";

export default {
  name: "companyPaymentDelayOverview",

  components: { apexchart, JsonViewer, LoopingRhombusesSpinner },
  mixins: [date],
  props: {
    invoices: {
      type: Array,
      required: true,
    },
    selectedYear: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      headers: [
        {
          text: "Invoice",
          align: "left",
          sortable: true,
          value: "invoice_name",
        },
        {
          text: "Last Transaction",
          align: "left",
          sortable: true,
          value: "last_transaction_name",
        },
        {
          text: "Invoice Date",
          align: "left",
          sortable: true,
          value: "invoice_date",
        },
        {
          text: "Expected Payment Date",
          align: "left",
          sortable: true,
          value: "expected_payment_date",
        },
        {
          text: "Last Payment Date",
          align: "left",
          sortable: true,
          value: "last_payment_date",
        },
        {
          text: "Payment Delay",
          align: "left",
          sortable: true,
          value: "payment_delay",
        },
      ],
      loading: false,
      payementStatsWindowIndex: 0,
      payementStatsWindowTitles: [
        "Payment Delay Overview",
        "Paid and Remaining Amounts",
        "Timeline",
      ],
      expeditionsIds: {
        onProgress: [],
        completed: [],
      },
      options: {
        colors: ["#ffb300", "#4caf50"],

        plotOptions: {
          bar: {
            horizontal: true,
            distributed: true,
            dataLabels: {
              hideOverflowingLabels: false,
            },
          },
        },
        chart: {
          height: 350,
          type: "rangeBar",
          events: {
            // click: this.clickOn,
          },
        },
        plotOptions: {
          bar: {
            horizontal: true,
          },
        },
        xaxis: {
          type: "datetime",
          // zoom: {
          //   autoScaleXaxis: true
          // }
        },
      },
    };
  },
  computed: {
    // ...mapGetters("commonStore", ["accountTypes"]),
    averagePaymentDelay() {
      var tempsum = 0;

      if (this.detailedData.length) {
        this.detailedData.forEach((dd) => {
          tempsum += dd.payment_delay;
        });

        return Math.ceil(tempsum / this.detailedData.length);
      }

      return 0;
    },
    detailedData() {
      var retItems = [];

      this.invoices.forEach((inv) => {
        // retrieving only invoices fully paid with data
        if (
          inv.reconciliation_status == "done" &&
          inv.bank_reconciliation &&
          inv.bank_reconciliation.bank_transactions
        ) {
          //init value
          var tempInvoice = {
            invoice_name: inv.designation,
            last_transaction_name: "",
            invoice_date: new Date(inv.invoice_date).getTime(),
            expected_payment_date: new Date(
              inv.expected_payment_date
            ).getTime(),
            last_payment_date: 0,
            payment_delay: 0,
            overdue: false,
          };

          //last payment
          var tempFurthestPayementDate = 0;
          inv.bank_reconciliation.bank_transactions.forEach((bt) => {
            tempFurthestPayementDate = new Date(bt.payment_date).getTime();
            if (tempFurthestPayementDate > tempInvoice.last_payment_date) {
              tempInvoice.last_payment_date = tempFurthestPayementDate;
              tempInvoice.last_transaction_name = bt.reference;

              tempInvoice.payment_delay =
                (tempInvoice.last_payment_date -
                  tempInvoice.expected_payment_date) /
                86400000;
            }

            //detecting if overdue or not
            tempInvoice.overdue =
              tempInvoice.last_payment_date > tempInvoice.expected_payment_date;
          });

          retItems.push(tempInvoice);
        }
      });
      return retItems;
    },
    series() {
      //retrieve all categories
      var onTimeArr = [];
      var overdueArr = [];
      var tempObj = {};
      var tempInvoiceDate = "";
      var tempFurthestPayementDate = "";

      this.invoices.forEach((inv) => {
        if (
          inv.reconciliation_status == "done" &&
          inv.bank_reconciliation &&
          inv.bank_reconciliation.bank_transactions
        ) {
          tempInvoiceDate = new Date(inv.invoice_date).getTime();
          tempObj = {
            x: inv.designation,
            y: [tempInvoiceDate, tempInvoiceDate],
          };

          inv.bank_reconciliation.bank_transactions.forEach((bt) => {
            tempFurthestPayementDate = new Date(bt.payment_date).getTime();
            if (tempFurthestPayementDate > tempObj.y[1]) {
              tempObj.y[1] = tempFurthestPayementDate;
            }
          });
          if (tempObj.y[1] > new Date(inv.expected_payment_date).getTime()) {
            overdueArr.push(tempObj);
          } else {
            onTimeArr.push(tempObj);
          }
        }
      });

      //   this.expeditions.forEach((exp) => {
      //     if (exp.eta && exp.etd) {
      //       if (this.stepsCompleted(exp) == exp.steps.length) {
      //         completedArr.push({
      //           x: this.contract_name + "." + exp.number,
      //           y: [new Date(exp.etd).getTime(), new Date(exp.eta).getTime()],
      //         });

      //         this.expeditionsIds.completed.push(exp.id);
      //       } else {
      //         onProgressArr.push({
      //           x: this.contract_name + "." + exp.number,
      //           y: [new Date(exp.etd).getTime(), new Date(exp.eta).getTime()],
      //         });
      //         this.expeditionsIds.onProgress.push(exp.id);
      //       }
      //     }
      //   });

      var retArr = [
        {
          name: "Overdue",
          data: overdueArr,
        },
        {
          name: "On Time",
          data: onTimeArr,
        },
      ];
      return retArr;
    },
  },
  methods: {
    payementStatsWindowIndexNext() {
      this.payementStatsWindowIndex++;
      if (this.payementStatsWindowIndex == 3) this.payementStatsWindowIndex = 0;
    },
    payementStatsWindowIndexPrevious() {
      this.payementStatsWindowIndex--;
      if (this.payementStatsWindowIndex == -1)
        this.payementStatsWindowIndex = 2;
    },
  },
};
</script>
<style>
.v-chip .aligncenter .v-chip__content {
  align-items: baseline !important;
}
</style>